// File Upload Component - Refactored with modular components
import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Progress } from '../ui/progress';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '../ui/select';
import { toast } from 'sonner';
import { 
  Upload, 
  File, 
  Trash2, 
  Download, 
  FileText, 
  Image, 
  Plus, 
  Loader2, 
  Pencil,
  Globe,
  Undo2,
  RotateCw,
  Star,
} from 'lucide-react';
import api from '../../services/api';

// Extracted components and utilities
import GalleryCard from './file-upload/GalleryCard';
import FilingCabinetCard from './file-upload/FilingCabinetCard';
import { useFileThumbnails } from './file-upload/useFileThumbnails';
import { FILE_CATEGORIES, getCategoryLabel, formatSize } from './file-upload/fileConstants';
import { getFileIcon, downloadFromUrl, isPreviewableFile } from './file-upload/fileHelpers';
import { useConfirm } from '../../components/common/ConfirmDialog';

const FileUpload = ({ 
  tenantId, 
  entityType, 
  entityId,
  currentProfilePhotoUrl: currentProfilePhotoUrlProp = '',
  onProfilePhotoSet,
  acceptedTypes = '*',
  maxSizeMB = 25,
  onUploadComplete,
  onDeleteComplete, // New callback when file is deleted
  compact = false,
  splitView = false, // New prop: splits into Gallery (images) and Filing Cabinet (documents)
  defaultCategory = '', // Auto-set category
  categoryReadOnly = false // Make category non-editable
}) => {
  const [files, setFiles] = useState([]);
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  // Local profile photo URL — starts from prop, updates immediately when user sets a new one
  // so the button hides and the badge shifts without waiting for a parent re-render.
  const [currentProfilePhotoUrl, setCurrentProfilePhotoUrl] = useState(currentProfilePhotoUrlProp);
  // Keep in sync if parent passes a new value (e.g. after navigation)
  useEffect(() => { setCurrentProfilePhotoUrl(currentProfilePhotoUrlProp); }, [currentProfilePhotoUrlProp]);
  const [uploading, setUploading] = useState(false);
  const [uploadSource, setUploadSource] = useState(null); // 'gallery' | 'cabinet' — set by which button triggered upload
  const [progress, setProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  
  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [isRotating, setIsRotating] = useState(false);

  // Rotate image 90° clockwise, delete old file, re-upload rotated version
  const handleRotateImage = async () => {
    if (!previewFile?.id || !previewUrl || isRotating) return;
    setIsRotating(true);
    try {
      const blob = await new Promise((resolve, reject) => {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalHeight;
          canvas.height = img.naturalWidth;
          const ctx = canvas.getContext('2d');
          ctx.translate(img.naturalHeight / 2, img.naturalWidth / 2);
          ctx.rotate(Math.PI / 2);
          ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
          canvas.toBlob(b => b ? resolve(b) : reject(new Error('Canvas toBlob failed')), 'image/jpeg', 0.92);
        };
        img.onerror = reject;
        img.src = previewUrl;
      });
      // Upload rotated version
      const fd = new FormData();
      fd.append('file', blob, previewFile.original_filename || 'rotated.jpg');
      if (previewFile.entity_id) fd.append('entity_id', previewFile.entity_id);
      if (previewFile.entity_type) fd.append('entity_type', previewFile.entity_type);
      if (previewFile.category) fd.append('category', previewFile.category);
      if (previewFile.horse_id) fd.append('horse_id', previewFile.horse_id);
      const uploadRes = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const newFile = uploadRes.data;
      // Delete old file
      await api.delete(`/files/${previewFile.id}`).catch(() => {});
      // Update state
      const newUrl = URL.createObjectURL(blob);
      setPreviewUrl(newUrl);
      setPreviewFile({ ...previewFile, id: newFile.id, original_filename: newFile.original_filename || previewFile.original_filename });
      setFiles(prev => prev.map(f => f.id === previewFile.id ? { ...f, id: newFile.id, original_filename: newFile.original_filename } : f));
      toast.success('Image rotated');
    } catch (err) {
      console.error('Rotate error:', err);
      toast.error('Failed to rotate image');
    } finally {
      setIsRotating(false);
    }
  };
  
  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editDescription, setEditDescription] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [updating, setUpdating] = useState(false);
  
  // File metadata state (for upload)
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [category, setCategory] = useState(defaultCategory || '');
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  
  const fileInputRef = useRef(null);

  // ── Client-side video compression ─────────────────────────────────────────
  // Downsamples to 1080p at ~2 Mbps using canvas + MediaRecorder so typical
  // 1-2 min iPhone clips land well under 30 MB before the network upload starts.
  // Videos already under 30 MB are returned as-is (no compression needed).
  const compressVideo = (file) => new Promise((resolve) => {
    // Skip compression if the file is already small enough — avoids unnecessary
    // real-time playback (30–120 s) and the timeout/stall failure modes.
    const COMPRESS_THRESHOLD = 30 * 1024 * 1024;  // 30 MB
    if (file.size <= COMPRESS_THRESHOLD) {
      resolve(file);
      return;
    }

    const video = document.createElement('video');
    const objectUrl = URL.createObjectURL(file);
    video.src = objectUrl;
    video.muted = true;
    video.playsInline = true;

    // Track animFrame so the timeout cleanup can cancel it
    let animFrame;
    // Failsafe: if compression hasn't finished in 90 s (e.g. video stalls,
    // onended never fires, screen lock on mobile), fall back to the original file.
    const failsafe = setTimeout(() => {
      cancelAnimationFrame(animFrame);
      try { if (recorder && recorder.state !== 'inactive') recorder.stop(); } catch {}
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    }, 90_000);

    let recorder;

    video.onloadedmetadata = () => {
      // Target 1080p; if video is already smaller, keep native resolution
      const MAX_HEIGHT = 1080;
      const scale = Math.min(1, MAX_HEIGHT / video.videoHeight);
      const width  = Math.round(video.videoWidth  * scale);
      const height = Math.round(video.videoHeight * scale);

      const canvas = document.createElement('canvas');
      canvas.width  = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      // Pick the best supported MIME type (prefer MP4, fall back to WebM)
      const mimeType = ['video/mp4', 'video/webm;codecs=h264', 'video/webm']
        .find(t => MediaRecorder.isTypeSupported(t)) || '';

      try {
        recorder = new MediaRecorder(canvas.captureStream(30), {
          mimeType,
          videoBitsPerSecond: 2_000_000,   // 2 Mbps → ~30 MB/min
        });
      } catch {
        // MediaRecorder not available or codec unsupported — skip compression
        clearTimeout(failsafe);
        URL.revokeObjectURL(objectUrl);
        resolve(file);
        return;
      }

      const chunks = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = () => {
        clearTimeout(failsafe);
        URL.revokeObjectURL(objectUrl);
        const blob = new Blob(chunks, { type: mimeType || file.type });
        // Only use compressed version if it is actually smaller (and non-empty)
        const outFile = blob.size > 0 && blob.size < file.size
          ? new File([blob], file.name.replace(/\.[^.]+$/, '.mp4'), { type: blob.type })
          : file;
        resolve(outFile);
      };

      // Draw frames in real-time while video plays
      const drawFrame = () => {
        ctx.drawImage(video, 0, 0, width, height);
        if (!video.paused && !video.ended) {
          animFrame = requestAnimationFrame(drawFrame);
          setCompressionProgress(
            video.duration > 0 ? Math.round((video.currentTime / video.duration) * 100) : 0
          );
        }
      };

      video.onplay = () => { drawFrame(); };
      video.onended = () => {
        clearTimeout(failsafe);
        cancelAnimationFrame(animFrame);
        setCompressionProgress(100);
        recorder.stop();
      };

      recorder.start(500);   // emit chunks every 500 ms
      video.play().catch(() => {
        // Autoplay blocked or codec issue — skip compression
        clearTimeout(failsafe);
        cancelAnimationFrame(animFrame);
        try { if (recorder && recorder.state !== 'inactive') recorder.stop(); } catch {}
        URL.revokeObjectURL(objectUrl);
        resolve(file);
      });
    };

    video.onerror = () => {
      clearTimeout(failsafe);
      cancelAnimationFrame(animFrame);
      URL.revokeObjectURL(objectUrl);
      resolve(file);   // graceful fallback
    };
  });

  // ── Upload handler ──────────────────────────────────────────────────────────
  const thumbnailUrls = useFileThumbnails(files);

  useEffect(() => {
    if (entityId) {
      fetchFiles();
    }
  }, [entityId, entityType]);
  
  // Update category when defaultCategory changes
  useEffect(() => {
    if (defaultCategory) {
      setCategory(defaultCategory);
    }
  }, [defaultCategory]);

  // Handle Preview Loading with proper error boundaries
  useEffect(() => {
    let isActive = true;
    let abortController = new AbortController();

    const loadPreview = async () => {
      // Validate prerequisites before proceeding
      if (!previewFile || !previewOpen) return;
      if (!previewFile.id) {
        console.warn('Preview file has no ID');
        return;
      }
      
      const mimeType = previewFile.mime_type || previewFile.file_type || '';
      const isPreviewable = 
        mimeType.startsWith('image/') ||
        mimeType.startsWith('video/') ||
        mimeType.startsWith('audio/') ||
        mimeType === 'application/pdf' ||
        mimeType.startsWith('text/');
      
      if (!isPreviewable) return;

      try {
        setPreviewLoading(true);
        
        // Revoke previous URL if exists
        if (previewUrl) {
          try {
            URL.revokeObjectURL(previewUrl);
          } catch (e) {
            // Ignore revoke errors
          }
          setPreviewUrl(null);
        }

        // Use fetch API instead of axios to avoid XMLHttpRequest responseText issues
        const token = localStorage.getItem('token');
        const baseUrl = window.location.origin;
        const response = await fetch(`${baseUrl}/api/files/${previewFile.id}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          signal: abortController.signal
        });

        if (!response.ok) {
          throw new Error(`Server error: ${response.status}`);
        }

        if (isActive) {
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          setPreviewUrl(url);
        }
      } catch (error) {
        // Only handle error if component is still mounted and not aborted
        if (isActive && error?.name !== 'AbortError') {
          console.warn('Preview load failed:', error?.message || 'Unknown error');
          toast.error('Failed to load file preview');
        }
      } finally {
        if (isActive) {
          setPreviewLoading(false);
        }
      }
    };

    if (previewOpen && previewFile?.id) {
      loadPreview();
    }

    return () => {
      isActive = false;
      abortController.abort();
    };
  }, [previewFile?.id, previewOpen]);

  // Cleanup URL on unmount or when closed
  useEffect(() => {
    if (!previewOpen && previewUrl) {
      try {
        URL.revokeObjectURL(previewUrl);
      } catch (e) {
        // Ignore cleanup errors
      }
      setPreviewUrl(null);
    }
  }, [previewOpen]);

  const fetchFiles = async () => {
    try {
      let params = {};
      if (entityType === 'horse') params = { horse_id: entityId };
      else if (entityType === 'crm_entity') params = { crm_entity_id: entityId };
      else if (entityType === 'sale_deliverable') params = { sale_deliverable_id: entityId };
      else if (entityType === 'sale_owner_deliverable') params = { sale_owner_deliverable_id: entityId };
      else if (entityType === 'invoice') params = { invoice_id: entityId };
      
      const response = await api.get('/files', { params });
      
      // Filter out hospitality receipts and photos from the "All Photos & Files" section
      // These should only appear in the Hospitality & Gifts section
      const filteredFiles = (response.data || []).filter(file => {
        // Exclude files with hospitality_gift_id
        if (file.hospitality_gift_id) return false;
        
        // Exclude files with description "Receipt" or "Photo" (uploaded from Hospitality section)
        const desc = (file.description || '').toLowerCase();
        if (desc === 'receipt' || desc === 'photo') return false;
        
        return true;
      });
      
      // Sort: most recent upload first
      filteredFiles.sort((a, b) => {
        const da = new Date(a.created_at || 0);
        const db = new Date(b.created_at || 0);
        return db - da;
      });

      setFiles(filteredFiles);
    } catch (error) {
      console.error('Failed to load files:', error);
      toast.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Videos get a higher size limit than other file types
    const isVideo = file.type.startsWith('video/');
    const effectiveMaxMB = isVideo ? 100 : maxSizeMB;
    if (file.size > effectiveMaxMB * 1024 * 1024) {
      toast.error(`File too large. Max size is ${effectiveMaxMB}MB${isVideo ? ' for videos' : ''}.`);
      return;
    }

    setSelectedFile(file);
    setUploadDialogOpen(true);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setProgress(0);

    // ── Client-side video compression ──────────────────────────────────────
    let fileToUpload = selectedFile;
    const COMPRESS_THRESHOLD = 30 * 1024 * 1024;  // must match threshold in compressVideo()
    const needsCompression = selectedFile.type.startsWith('video/') && selectedFile.size > COMPRESS_THRESHOLD;
    if (needsCompression) {
      setIsCompressing(true);
      setCompressionProgress(0);
      try {
        fileToUpload = await compressVideo(selectedFile);
      } catch {
        fileToUpload = selectedFile;  // graceful fallback to original
      } finally {
        setIsCompressing(false);
        setCompressionProgress(0);
      }
    }

    const formData = new FormData();
    formData.append('file', fileToUpload);
    formData.append('description', description);
    if (notes) formData.append('notes', notes);
    if (category) formData.append('category', category);
    if (uploadSource) formData.append('upload_source', uploadSource);
    
    if (entityType === 'horse') formData.append('horse_id', entityId);
    else if (entityType === 'crm_entity') formData.append('crm_entity_id', entityId);
    else if (entityType === 'sale_deliverable') formData.append('sale_deliverable_id', entityId);
    else if (entityType === 'sale_owner_deliverable') formData.append('sale_owner_deliverable_id', entityId);
    else if (entityType === 'invoice') formData.append('invoice_id', entityId);

    try {
      const response = await api.post('/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        // Give video uploads up to 10 minutes on slow connections
        ...(fileToUpload.type.startsWith('video/') && { timeout: 10 * 60 * 1000 }),
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setProgress(percentCompleted);
        }
      });

      toast.success('File uploaded');
      fetchFiles();
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setDescription('');
      setNotes('');
      setCategory(defaultCategory || ''); // Reset to default category, not empty
      
      // Pass file info to callback
      if (onUploadComplete && response.data) {
        // Use description if available, otherwise use filename
        const displayName = response.data.description || description || response.data.filename;
        onUploadComplete(response.data.url, displayName);
      }
      
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      console.error(error);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleDelete = async (fileId, e) => {
    if (e) e.stopPropagation();
    if (!await confirm({ title: 'Are you sure you want to delete this file?', destructive: true })) return;

    try {
      await api.delete(`/files/${fileId}`);
      toast.success('File deleted');
      setFiles(files.filter(f => f.id !== fileId));
      // Close preview if deleting the previewed file
      if (previewFile?.id === fileId) {
        setPreviewOpen(false);
        setPreviewFile(null);
      }
      
      // Notify parent component that file was deleted
      if (onDeleteComplete) {
        onDeleteComplete(fileId);
      }
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const handleUpdateFile = async () => {
    if (!previewFile?.id) return;
    
    setUpdating(true);
    try {
      const formData = new FormData();
      formData.append('description', editDescription);
      formData.append('notes', editNotes);
      formData.append('category', editCategory);
      
      const response = await api.put(`/files/${previewFile.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Update local state
      setFiles(files.map(f => f.id === previewFile.id ? { ...f, description: editDescription, notes: editNotes, category: editCategory } : f));
      setPreviewFile({ ...previewFile, description: editDescription, notes: editNotes, category: editCategory });
      setIsEditing(false);
      toast.success('File updated');
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update file');
    } finally {
      setUpdating(false);
    }
  };

  const startEditing = () => {
    setEditDescription(previewFile?.description || previewFile?.original_filename || '');
    setEditNotes(previewFile?.notes || '');
    setEditCategory(previewFile?.category || '');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditDescription('');
    setEditNotes('');
    setEditCategory('');
  };

  // Render file preview based on mime type
  const renderFilePreview = () => {
    if (previewLoading) {
      return <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />;
    }

    const mimeType = previewFile?.mime_type || previewFile?.file_type || '';
    
    // Image preview
    if (mimeType.startsWith('image/')) {
      return previewUrl ? (
        <img src={previewUrl} alt="Preview" className="max-h-[60vh] object-contain" />
      ) : (
        <p className="text-muted-foreground text-sm">Image preview not available</p>
      );
    }
    
    // PDF preview
    if (mimeType === 'application/pdf') {
      return previewUrl ? (
        <object 
          data={previewUrl} 
          type="application/pdf"
          className="w-full h-[60vh]"
        >
          <p className="text-muted-foreground text-sm p-4 text-center">
            Unable to display PDF. <a href={previewUrl} download className="text-primary underline">Download instead</a>
          </p>
        </object>
      ) : (
        <p className="text-muted-foreground text-sm">PDF preview not available</p>
      );
    }
    
    // Video preview
    if (mimeType.startsWith('video/')) {
      return previewUrl ? (
        <video 
          src={previewUrl} 
          controls 
          className="max-h-[60vh] max-w-full"
        >
          Your browser does not support video playback.
        </video>
      ) : (
        <p className="text-muted-foreground text-sm">Video preview not available</p>
      );
    }
    
    // Audio preview
    if (mimeType.startsWith('audio/')) {
      return previewUrl ? (
        <div className="flex flex-col items-center gap-4">
          <FileText className="w-16 h-16 text-muted-foreground" />
          <audio src={previewUrl} controls className="w-full max-w-md">
            Your browser does not support audio playback.
          </audio>
        </div>
      ) : (
        <p className="text-muted-foreground text-sm">Audio preview not available</p>
      );
    }
    
    // Text file preview
    if (mimeType.startsWith('text/')) {
      return previewUrl ? (
        <iframe 
          src={previewUrl} 
          title="Text Preview"
          className="w-full h-[60vh] border rounded bg-white"
        />
      ) : (
        <p className="text-muted-foreground text-sm">Text preview not available</p>
      );
    }
    
    // Unsupported file type
    return (
      <div className="flex flex-col items-center gap-2 text-muted-foreground">
        <FileText className="w-16 h-16" />
        <p className="text-sm">Preview not available for this file type</p>
        <p className="text-xs">({mimeType || 'Unknown type'})</p>
      </div>
    );
  };

  const downloadFile = async (fileId, filename, e) => {
    if (e) e.stopPropagation();
    try {
      const response = await api.get(`/files/${fileId}`, {
        responseType: 'blob' 
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Download failed');
    }
  };

  const onCardClick = (file, e) => {
    // Prevent event bubbling
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    // Validate file object
    if (!file || !file.id) {
      console.warn('Invalid file clicked');
      return;
    }
    
    const mimeType = file.mime_type || file.file_type || '';
    const isPreviewable = 
      mimeType.startsWith('image/') ||
      mimeType.startsWith('video/') ||
      mimeType.startsWith('audio/') ||
      mimeType === 'application/pdf' ||
      mimeType.startsWith('text/');
    
    if (isPreviewable) {
      // Close any existing preview first to reset state
      setPreviewOpen(false);
      setPreviewUrl(null);
      
      // Small delay to ensure state is reset before opening new preview
      setTimeout(() => {
        setPreviewFile(file);
        setPreviewOpen(true);
      }, 50);
    } else {
      // For non-previewable files, download directly
      downloadFile(file.id, file.original_filename, e);
    }
  };

  // Compact View (for Overview Tab)
  if (compact) {
    return (
      <div className="space-y-4" data-testid="file-upload-compact">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {files.map(file => (
            <div 
              key={file.id} 
              data-testid={`file-tile-${file.id}`}
              className="relative group border rounded-md p-2 hover:shadow-md cursor-pointer bg-white"
              onClick={(e) => onCardClick(file, e)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onCardClick(file, e);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className="aspect-square bg-muted flex items-center justify-center rounded-sm overflow-hidden mb-2 pointer-events-none">
                {file.mime_type?.startsWith('image/') ? (
                   <Image className="w-8 h-8 text-muted-foreground" />
                ) : (
                   getFileIcon(file.mime_type)
                )}
              </div>
              <p className="text-xs font-medium truncate text-center pointer-events-none" title={file.description || file.original_filename}>
                {file.description || file.original_filename}
              </p>
            </div>
          ))}
          
          {/* Add Button Tile */}
          <div 
            data-testid="file-upload-add-btn"
            className="border-2 border-dashed rounded-md flex flex-col items-center justify-center aspect-square cursor-pointer hover:bg-muted/20"
            onClick={() => fileInputRef.current?.click()}
          >
            <Plus className="w-6 h-6 text-muted-foreground mb-1" />
            <span className="text-xs text-muted-foreground">Add</span>
          </div>
        </div>

        {/* Hidden Input & Dialog Logic reused */}
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          onChange={handleFileSelect}
          accept={acceptedTypes}
        />
        
        {/* Upload Dialog */}
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload File</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-4 border rounded bg-muted/20">
                <p className="font-medium text-sm truncate">{selectedFile?.name}</p>
                <p className="text-xs text-muted-foreground">{selectedFile && formatSize(selectedFile.size)}</p>
              </div>
              <div className="space-y-2">
                <Label>Description / Name</Label>
                <Input 
                  value={description} 
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g. Sales Contract, ID Photo" 
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={category} onValueChange={setCategory} disabled={categoryReadOnly}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select file type" />
                  </SelectTrigger>
                  <SelectContent>
                    {FILE_CATEGORIES.map(group => (
                      <SelectGroup key={group.group}>
                        <SelectLabel>{group.group}</SelectLabel>
                        {group.items.map(item => (
                          <SelectItem key={item.value} value={item.value}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea 
                  value={notes} 
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Additional notes about this file..."
                  rows={2}
                />
              </div>
              {isCompressing && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>Compressing video…</span>
                    <span>{compressionProgress}%</span>
                  </div>
                  <Progress value={compressionProgress} />
                </div>
              )}
              {uploading && !isCompressing && <Progress value={progress} />}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUploadDialogOpen(false)} disabled={uploading || isCompressing}>Cancel</Button>
              <Button onClick={handleUpload} disabled={uploading || isCompressing}>
                {isCompressing ? 'Compressing…' : uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={previewOpen} onOpenChange={(open) => {
          setPreviewOpen(open);
          if (!open) {
            setIsEditing(false);
            setEditDescription('');
            setEditNotes('');
            setEditCategory('');
          }
        }}>
          <DialogContent className="max-w-3xl" data-testid="file-preview-dialog">
            <DialogHeader className="flex flex-row items-center justify-between">
              {isEditing ? (
                <div className="flex-1 mr-4 space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Name</Label>
                    <Input 
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder="Enter file name/description"
                      data-testid="edit-file-name-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Type</Label>
                    <Select value={editCategory} onValueChange={setEditCategory}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select file type" />
                      </SelectTrigger>
                      <SelectContent className="z-[100]">
                        {FILE_CATEGORIES.map(group => (
                          <SelectGroup key={group.group}>
                            <SelectLabel>{group.group}</SelectLabel>
                            {group.items.map(item => (
                              <SelectItem key={item.value} value={item.value}>
                                {item.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Notes</Label>
                    <Textarea 
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      placeholder="Additional notes..."
                      rows={2}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex-1">
                  <DialogTitle>{previewFile?.description || previewFile?.original_filename}</DialogTitle>
                  {previewFile?.category && (
                    <p className="text-xs text-primary mt-1">{getCategoryLabel(previewFile.category)}</p>
                  )}
                  {previewFile?.notes && (
                    <p className="text-xs text-muted-foreground mt-1">{previewFile.notes}</p>
                  )}
                </div>
              )}
            </DialogHeader>
            <div className="flex items-center justify-center p-4 bg-black/5 rounded min-h-[200px]">
              {renderFilePreview()}
            </div>
            <DialogFooter className="flex justify-between sm:justify-between">
              <div className="flex gap-2">
                {isEditing ? (
                  <>
                    <Button 
                      variant="outline" 
                      onClick={cancelEditing}
                      disabled={updating}
                      data-testid="cancel-edit-btn"
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleUpdateFile}
                      disabled={updating || !editDescription.trim()}
                      data-testid="save-file-name-btn"
                    >
                      {updating ? 'Saving...' : 'Save'}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={startEditing}
                      title="Edit name"
                      data-testid="edit-file-btn"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={(e) => handleDelete(previewFile?.id, e)}
                      title="Delete file"
                      data-testid="delete-file-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleRotateImage}
                      disabled={isRotating || !previewFile?.mime_type?.startsWith('image/')}
                      title="Rotate 90° clockwise"
                      data-testid="rotate-file-btn"
                    >
                      <RotateCw className={`w-4 h-4 ${isRotating ? 'animate-spin' : ''}`} />
                    </Button>
                  </>
                )}
              </div>
              {!isEditing && (
                <div className="flex flex-col gap-2">
                  {previewFile?.published_to_portal && (
                    <div className="flex items-center gap-2 border rounded-md px-3 py-1.5 bg-blue-50 border-blue-200">
                      <Globe className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                      <span className="text-xs text-blue-700">
                        Published to Portal{previewFile.published_at
                          ? ` on ${new Date(previewFile.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}`
                          : ''}
                        {previewFile.published_by ? ` by ${previewFile.published_by}` : ''}
                      </span>
                    </div>
                  )}
                  <div className="flex gap-2">
                    {entityType !== 'sale_owner_deliverable' && (
                      <>
                        {previewFile?.published_to_portal ? (
                          <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-red-600 text-xs h-8"
                            onClick={async () => {
                              try {
                                const res = await api.post(`/files/${previewFile.id}/publish-to-portal`);
                                const updated = { ...previewFile, published_to_portal: res.data.published_to_portal, published_at: res.data.published_at, published_by: res.data.published_by };
                                setPreviewFile(updated);
                                setFiles(prev => prev.map(f => f.id === previewFile.id ? { ...f, ...updated } : f));
                                toast.success('Unpublished from portal');
                              } catch { toast.error('Failed to update'); }
                            }}>
                            <Undo2 className="w-3.5 h-3.5 mr-1" /> Undo Publish
                          </Button>
                        ) : (
                          <Button variant="outline" size="sm"
                            onClick={async () => {
                              try {
                                const res = await api.post(`/files/${previewFile.id}/publish-to-portal`);
                                const updated = { ...previewFile, published_to_portal: res.data.published_to_portal, published_at: res.data.published_at, published_by: res.data.published_by };
                                setPreviewFile(updated);
                                setFiles(prev => prev.map(f => f.id === previewFile.id ? { ...f, ...updated } : f));
                                toast.success('Published to portal');
                              } catch { toast.error('Failed to update'); }
                            }}>
                            <Upload className="w-4 h-4 mr-1" /> Publish
                          </Button>
                        )}
                        {/* Set as Profile Photo — filled star + disabled when already set */}
                        {previewFile?.mime_type?.startsWith('image/') && ['horse', 'crm_entity'].includes(entityType) && (() => {
                          const isProfile = currentProfilePhotoUrl?.includes(previewFile.id);
                          return (
                            <Button variant="outline" size="sm" data-testid="set-profile-photo-btn"
                              disabled={isProfile}
                              onClick={async () => {
                                try {
                                  await api.post(`/files/${previewFile.id}/set-as-profile-photo`);
                                  const newUrl = `/api/files/public/${previewFile.id}`;
                                  setCurrentProfilePhotoUrl(newUrl);
                                  if (onProfilePhotoSet) onProfilePhotoSet(newUrl);
                                  toast.success('Profile photo updated');
                                } catch { toast.error('Failed to set profile photo'); }
                              }}>
                              <Star className={`w-4 h-4 mr-1 ${isProfile ? 'fill-current' : ''}`} />
                              Set as Profile Photo
                            </Button>
                          );
                        })()}
                      </>
                    )}
                    <Button 
                      size="sm"
                      onClick={() => downloadFromUrl(previewUrl, previewFile?.original_filename)} 
                      disabled={!previewUrl}
                      data-testid="download-file-btn"
                    >
                      <Download className="w-4 h-4 mr-2" /> Download
                    </Button>
                  </div>
                </div>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Split files using upload_source (button-intent) as primary signal.
  // Falls back to MIME type for legacy files that predate upload_source.
  const mediaFiles = files.filter(f =>
    f.upload_source === 'gallery' ||
    (!f.upload_source && (f.mime_type?.startsWith('image/') || f.mime_type?.startsWith('video/')))
  );
  const documentFiles = files.filter(f =>
    f.upload_source === 'cabinet' ||
    (!f.upload_source && !f.mime_type?.startsWith('image/') && !f.mime_type?.startsWith('video/'))
  );

  // Split View: Gallery + Filing Cabinet
  if (splitView) {
    return (
      <div className="space-y-6">
        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          onChange={handleFileSelect}
          accept={acceptedTypes}
        />

        {/* Gallery Card Component */}
        <GalleryCard
          mediaFiles={mediaFiles}
          thumbnailUrls={thumbnailUrls}
          onAddMedia={() => { setUploadSource('gallery'); fileInputRef.current?.click(); }}
          onFileClick={onCardClick}
          onDownload={downloadFile}
          onDelete={handleDelete}
          uploading={uploading}
        />

        {/* Filing Cabinet Card Component */}
        <FilingCabinetCard
          documentFiles={documentFiles}
          onAddFile={() => { setUploadSource('cabinet'); fileInputRef.current?.click(); }}
          onFileClick={onCardClick}
          onDownload={downloadFile}
          onDelete={handleDelete}
          uploading={uploading}
        />

        {/* Upload Dialog */}
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload File</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-4 border rounded bg-muted/20">
                <p className="font-medium text-sm truncate">{selectedFile?.name}</p>
                <p className="text-xs text-muted-foreground">{selectedFile && formatSize(selectedFile.size)}</p>
              </div>
              <div className="space-y-2">
                <Label>Description / Name</Label>
                <Input 
                  value={description} 
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g. Sales Contract, ID Photo" 
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={category} onValueChange={setCategory} disabled={categoryReadOnly}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select file type" />
                  </SelectTrigger>
                  <SelectContent>
                    {FILE_CATEGORIES.map(group => (
                      <SelectGroup key={group.group}>
                        <SelectLabel>{group.group}</SelectLabel>
                        {group.items.map(item => (
                          <SelectItem key={item.value} value={item.value}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea 
                  value={notes} 
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Additional notes about this file..."
                  rows={2}
                />
              </div>
              {isCompressing && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>Compressing video…</span>
                    <span>{compressionProgress}%</span>
                  </div>
                  <Progress value={compressionProgress} />
                </div>
              )}
              {uploading && !isCompressing && <Progress value={progress} />}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUploadDialogOpen(false)} disabled={uploading || isCompressing}>Cancel</Button>
              <Button onClick={handleUpload} disabled={uploading || isCompressing}>
                {isCompressing ? 'Compressing…' : uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={previewOpen} onOpenChange={(open) => {
          setPreviewOpen(open);
          if (!open) {
            setIsEditing(false);
            setEditDescription('');
            setEditNotes('');
            setEditCategory('');
          }
        }}>
          <DialogContent className="max-w-3xl" data-testid="file-preview-dialog">
            <DialogHeader className="flex flex-row items-center justify-between">
              {isEditing ? (
                <div className="flex-1 mr-4 space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Name</Label>
                    <Input 
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder="Enter file name/description"
                      data-testid="edit-file-name-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Type</Label>
                    <Select value={editCategory} onValueChange={setEditCategory}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select file type" />
                      </SelectTrigger>
                      <SelectContent className="z-[100]">
                        {FILE_CATEGORIES.map(group => (
                          <SelectGroup key={group.group}>
                            <SelectLabel>{group.group}</SelectLabel>
                            {group.items.map(item => (
                              <SelectItem key={item.value} value={item.value}>
                                {item.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Notes</Label>
                    <Textarea 
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      placeholder="Additional notes..."
                      rows={2}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex-1">
                  <DialogTitle>{previewFile?.description || previewFile?.original_filename}</DialogTitle>
                  {previewFile?.category && (
                    <p className="text-xs text-primary mt-1">{getCategoryLabel(previewFile.category)}</p>
                  )}
                  {previewFile?.notes && (
                    <p className="text-xs text-muted-foreground mt-1">{previewFile.notes}</p>
                  )}
                </div>
              )}
            </DialogHeader>
            <div className="flex items-center justify-center p-4 bg-black/5 rounded min-h-[200px]">
              {renderFilePreview()}
            </div>
            <DialogFooter className="flex justify-between sm:justify-between">
              <div className="flex gap-2">
                {isEditing ? (
                  <>
                    <Button 
                      variant="outline" 
                      onClick={cancelEditing}
                      disabled={updating}
                      data-testid="cancel-edit-btn"
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleUpdateFile}
                      disabled={updating}
                      data-testid="save-edit-btn"
                    >
                      {updating ? 'Saving...' : 'Save'}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={startEditing}
                      title="Edit name"
                      data-testid="edit-file-btn"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={(e) => handleDelete(previewFile?.id, e)}
                      title="Delete file"
                      data-testid="delete-file-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleRotateImage}
                      disabled={isRotating || !previewFile?.mime_type?.startsWith('image/')}
                      title="Rotate 90° clockwise"
                      data-testid="rotate-file-btn"
                    >
                      <RotateCw className={`w-4 h-4 ${isRotating ? 'animate-spin' : ''}`} />
                    </Button>
                  </>
                )}
              </div>
              {!isEditing && (
                <div className="flex flex-col gap-2">
                  {previewFile?.published_to_portal && (
                    <div className="flex items-center gap-2 border rounded-md px-3 py-1.5 bg-blue-50 border-blue-200">
                      <Globe className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                      <span className="text-xs text-blue-700">
                        Published to Portal{previewFile.published_at
                          ? ` on ${new Date(previewFile.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}`
                          : ''}
                        {previewFile.published_by ? ` by ${previewFile.published_by}` : ''}
                      </span>
                    </div>
                  )}
                  <div className="flex gap-2">
                    {entityType !== 'sale_owner_deliverable' && (
                      <>
                        {previewFile?.published_to_portal ? (
                          <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-red-600 text-xs h-8"
                            onClick={async () => {
                              try {
                                const res = await api.post(`/files/${previewFile.id}/publish-to-portal`);
                                const updated = { ...previewFile, published_to_portal: res.data.published_to_portal, published_at: res.data.published_at, published_by: res.data.published_by };
                                setPreviewFile(updated);
                                setFiles(prev => prev.map(f => f.id === previewFile.id ? { ...f, ...updated } : f));
                                toast.success('Unpublished from portal');
                              } catch { toast.error('Failed to update'); }
                            }}>
                            <Undo2 className="w-3.5 h-3.5 mr-1" /> Undo Publish
                          </Button>
                        ) : (
                          <Button variant="outline" size="sm"
                            onClick={async () => {
                              try {
                                const res = await api.post(`/files/${previewFile.id}/publish-to-portal`);
                                const updated = { ...previewFile, published_to_portal: res.data.published_to_portal, published_at: res.data.published_at, published_by: res.data.published_by };
                                setPreviewFile(updated);
                                setFiles(prev => prev.map(f => f.id === previewFile.id ? { ...f, ...updated } : f));
                                toast.success('Published to portal');
                              } catch { toast.error('Failed to update'); }
                            }}>
                            <Upload className="w-4 h-4 mr-1" /> Publish
                          </Button>
                        )}
                        {/* Set as Profile Photo — filled star + disabled when already set */}
                        {previewFile?.mime_type?.startsWith('image/') && ['horse', 'crm_entity'].includes(entityType) && (() => {
                          const isProfile = currentProfilePhotoUrl?.includes(previewFile.id);
                          return (
                            <Button variant="outline" size="sm" data-testid="set-profile-photo-btn"
                              disabled={isProfile}
                              onClick={async () => {
                                try {
                                  await api.post(`/files/${previewFile.id}/set-as-profile-photo`);
                                  const newUrl = `/api/files/public/${previewFile.id}`;
                                  setCurrentProfilePhotoUrl(newUrl);
                                  if (onProfilePhotoSet) onProfilePhotoSet(newUrl);
                                  toast.success('Profile photo updated');
                                } catch { toast.error('Failed to set profile photo'); }
                              }}>
                              <Star className={`w-4 h-4 mr-1 ${isProfile ? 'fill-current' : ''}`} />
                              Set as Profile Photo
                            </Button>
                          );
                        })()}
                      </>
                    )}
                    <Button 
                      size="sm"
                      onClick={() => downloadFromUrl(previewUrl, previewFile?.original_filename)} 
                      disabled={!previewUrl}
                      data-testid="download-file-btn"
                    >
                      <Download className="w-4 h-4 mr-2" /> Download
                    </Button>
                  </div>
                </div>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Standard Gallery View (all files as tiles)
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          onChange={handleFileSelect}
          accept={acceptedTypes}
        />
        <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} variant="outline">
          <Upload className="w-4 h-4 mr-2" /> Upload File
        </Button>
      </div>

      {files.length === 0 && !loading ? (
        <div className="text-center py-12 border-2 border-dashed rounded-lg">
          <Image className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground">No photos or files yet</p>
          <Button 
            variant="outline" 
            size="sm" 
            className="mt-3"
            onClick={() => fileInputRef.current?.click()}
          >
            <Plus className="w-4 h-4 mr-2" /> Add First File
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {files.map((file) => (
            <div 
              key={file.id} 
              className="group relative border rounded-lg overflow-hidden bg-muted/10 hover:shadow-lg transition-shadow cursor-pointer"
              onClick={(e) => onCardClick(file, e)}
              data-testid={`file-tile-${file.id}`}
            >
              {/* Thumbnail */}
              <div className="aspect-square bg-muted/30 flex items-center justify-center overflow-hidden relative">
                {file.published_to_portal && (
                  <div className="absolute top-1 left-1 z-10">
                    <div className="bg-primary text-white text-[9px] px-1.5 py-0.5 rounded font-medium">Published</div>
                  </div>
                )}
                {file.mime_type?.startsWith('image/') && thumbnailUrls[file.id] ? (
                  <img 
                    src={thumbnailUrls[file.id]}
                    alt={file.description || file.original_filename}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center">
                    {file.mime_type?.startsWith('image/') ? (
                      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    ) : (
                      <>
                        {getFileIcon(file.mime_type)}
                        <span className="text-[10px] text-muted-foreground mt-1 uppercase">
                          {file.original_filename?.split('.').pop() || 'file'}
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
              
              {/* File Info */}
              <div className="p-2">
                <p className="text-xs font-medium truncate" title={file.description || file.original_filename}>
                  {file.description || file.original_filename}
                </p>
                <div className="flex items-center gap-1 mt-0.5">
                  <p className="text-[10px] text-muted-foreground">
                    {formatSize(file.file_size_bytes)}
                  </p>
                  {file.from_note && (
                    <span className="text-[9px] bg-amber-100 text-amber-700 px-1 py-0.5 rounded font-medium">From Note</span>
                  )}
                </div>
              </div>
              
              {/* Hover Actions */}
              <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                <Button 
                  variant="secondary" 
                  size="icon"
                  className="h-7 w-7 bg-white/90 hover:bg-white shadow-sm"
                  onClick={async (e) => { e.stopPropagation(); downloadFile(file.id, file.original_filename, e); }}
                >
                  <Download className="w-3.5 h-3.5" />
                </Button>
                <Button 
                  variant="secondary" 
                  size="icon"
                  className="h-7 w-7 bg-white/90 hover:bg-red-50 text-destructive shadow-sm"
                  onClick={async (e) => { e.stopPropagation(); handleDelete(file.id, e); }}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
          
          {/* Add More Tile */}
          <div 
            className="aspect-square border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-muted/20 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <Plus className="w-8 h-8 text-muted-foreground/50" />
            <span className="text-xs text-muted-foreground mt-1">Add</span>
          </div>
        </div>
      )}

      {/* Upload Dialog Reuse */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload File</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-4 border rounded bg-muted/20">
                <p className="font-medium text-sm truncate">{selectedFile?.name}</p>
                <p className="text-xs text-muted-foreground">{selectedFile && formatSize(selectedFile.size)}</p>
              </div>
              <div className="space-y-2">
                <Label>Description / Name</Label>
                <Input 
                  value={description} 
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g. Sales Contract, ID Photo" 
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={category} onValueChange={setCategory} disabled={categoryReadOnly}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select file type" />
                  </SelectTrigger>
                  <SelectContent>
                    {FILE_CATEGORIES.map(group => (
                      <SelectGroup key={group.group}>
                        <SelectLabel>{group.group}</SelectLabel>
                        {group.items.map(item => (
                          <SelectItem key={item.value} value={item.value}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea 
                  value={notes} 
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Additional notes about this file..."
                  rows={2}
                />
              </div>
              {isCompressing && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>Compressing video…</span>
                    <span>{compressionProgress}%</span>
                  </div>
                  <Progress value={compressionProgress} />
                </div>
              )}
              {uploading && !isCompressing && <Progress value={progress} />}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUploadDialogOpen(false)} disabled={uploading || isCompressing}>Cancel</Button>
              <Button onClick={handleUpload} disabled={uploading || isCompressing}>
                {isCompressing ? 'Compressing…' : uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={(open) => {
        setPreviewOpen(open);
        if (!open) {
          setIsEditing(false);
          setEditDescription('');
          setEditNotes('');
          setEditCategory('');
        }
      }}>
        <DialogContent className="max-w-3xl" data-testid="file-preview-dialog">
          <DialogHeader className="flex flex-row items-center justify-between">
            {isEditing ? (
              <div className="flex-1 mr-4 space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs">Name</Label>
                  <Input 
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder="Enter file name/description"
                    data-testid="edit-file-name-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Type</Label>
                  <Select value={editCategory} onValueChange={setEditCategory}>
                    <SelectTrigger data-testid="edit-file-type-select">
                      <SelectValue placeholder="Select file type" />
                    </SelectTrigger>
                    <SelectContent className="z-[100]">
                      {FILE_CATEGORIES.map(group => (
                        <SelectGroup key={group.group}>
                          <SelectLabel>{group.group}</SelectLabel>
                          {group.items.map(item => (
                            <SelectItem key={item.value} value={item.value}>
                              {item.label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Notes</Label>
                  <Textarea 
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    placeholder="Additional notes..."
                    rows={2}
                  />
                </div>
              </div>
            ) : (
              <div className="flex-1">
                <DialogTitle>{previewFile?.description || previewFile?.original_filename}</DialogTitle>
                {previewFile?.category && (
                  <p className="text-xs text-primary mt-1">{getCategoryLabel(previewFile.category)}</p>
                )}
                {previewFile?.notes && (
                  <p className="text-xs text-muted-foreground mt-1">{previewFile.notes}</p>
                )}
              </div>
            )}
          </DialogHeader>
          <div className="flex items-center justify-center p-4 bg-black/5 rounded min-h-[200px]">
            {renderFilePreview()}
          </div>
          <DialogFooter className="flex flex-col gap-2 sm:flex-col items-stretch">
            {/* Published details */}
            {!isEditing && previewFile?.published_to_portal && (
              <div className="flex items-center gap-2 border rounded-md px-3 py-1.5 bg-blue-50 border-blue-200">
                <Globe className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                <span className="text-xs text-blue-700">
                  Published to Portal{previewFile.published_at
                    ? ` on ${new Date(previewFile.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}`
                    : ''}
                  {previewFile.published_by ? ` by ${previewFile.published_by}` : ''}
                </span>
              </div>
            )}
            <div className="flex justify-between sm:justify-between w-full">
              <div className="flex gap-2">
                {isEditing ? (
                  <>
                    <Button variant="outline" onClick={cancelEditing} disabled={updating} data-testid="cancel-edit-btn">Cancel</Button>
                    <Button onClick={handleUpdateFile} disabled={updating || !editDescription.trim()} data-testid="save-file-name-btn">
                      {updating ? 'Saving...' : 'Save'}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" size="icon" onClick={startEditing} title="Edit name" data-testid="edit-file-btn">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="icon" className="text-destructive hover:text-destructive"
                      onClick={(e) => handleDelete(previewFile?.id, e)} title="Delete file" data-testid="delete-file-btn">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                      <Button variant="outline" size="icon"
                        onClick={handleRotateImage}
                        disabled={isRotating || !previewFile?.mime_type?.startsWith('image/')}
                        title="Rotate 90° clockwise" data-testid="rotate-file-btn">
                        <RotateCw className={`w-4 h-4 ${isRotating ? 'animate-spin' : ''}`} />
                      </Button>
                  </>
                )}
              </div>
              {!isEditing && (
                <div className="flex gap-2">
                  {entityType !== 'sale_owner_deliverable' && (
                    previewFile?.published_to_portal ? (
                      <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-red-600 text-xs"
                        onClick={async () => {
                          try {
                            const res = await api.post(`/files/${previewFile.id}/publish-to-portal`);
                            const updated = { ...previewFile, published_to_portal: res.data.published_to_portal, published_at: res.data.published_at, published_by: res.data.published_by };
                            setPreviewFile(updated);
                            setFiles(prev => prev.map(f => f.id === previewFile.id ? { ...f, ...updated } : f));
                            toast.success('Unpublished from portal');
                          } catch { toast.error('Failed to update'); }
                        }}>
                        <Undo2 className="w-3.5 h-3.5 mr-1" /> Undo Publish
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm"
                        onClick={async () => {
                          try {
                            const res = await api.post(`/files/${previewFile.id}/publish-to-portal`);
                            const updated = { ...previewFile, published_to_portal: res.data.published_to_portal, published_at: res.data.published_at, published_by: res.data.published_by };
                            setPreviewFile(updated);
                            setFiles(prev => prev.map(f => f.id === previewFile.id ? { ...f, ...updated } : f));
                            toast.success('Published to portal');
                          } catch { toast.error('Failed to update'); }
                        }}>
                        <Upload className="w-4 h-4 mr-1" /> Publish
                      </Button>
                    )
                  )}
                  {/* Set as Profile Photo — filled star + disabled when already set */}
                  {previewFile?.mime_type?.startsWith('image/') && ['horse', 'crm_entity'].includes(entityType) && (() => {
                    const isProfile = currentProfilePhotoUrl?.includes(previewFile.id);
                    return (
                      <Button variant="outline" size="sm" data-testid="set-profile-photo-btn"
                        disabled={isProfile}
                        onClick={async () => {
                          try {
                            await api.post(`/files/${previewFile.id}/set-as-profile-photo`);
                            const newUrl = `/api/files/public/${previewFile.id}`;
                            setCurrentProfilePhotoUrl(newUrl);
                            if (onProfilePhotoSet) onProfilePhotoSet(newUrl);
                            toast.success('Profile photo updated');
                          } catch { toast.error('Failed to set profile photo'); }
                        }}>
                        <Star className={`w-4 h-4 mr-1 ${isProfile ? 'fill-current' : ''}`} />
                        Set as Profile Photo
                      </Button>
                    );
                  })()}
                  <Button size="sm" onClick={() => downloadFromUrl(previewUrl, previewFile?.original_filename)}
                    disabled={!previewUrl} data-testid="download-file-btn">
                    <Download className="w-4 h-4 mr-2" /> Download
                  </Button>
                </div>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FileUpload;
